import { isEnrichedTimeseriesRef } from '#core/lib/timeseries/enrichedRef.ts';

import type { DatasetRow } from '#plugin-chat/lib/data-pipeline/types.ts';

/**
 * Extract timeseries externalIds from the named columns of a dataset's rows.
 *
 * Post timeseries-ref enrichment (commit 0217427c), the row value at a
 * timeseries-ref column is an `EnrichedTimeseriesRef` object
 * (`{ externalId, unit, datapoints, ... }`), not a bare string. The extractor
 * accepts either shape so callers can reference:
 * - the rich-ref column directly (e.g. `priorityOilRateStd`) — typical case
 *   for datasets coming straight from `queryKnowledgeGraph`,
 * - a pre-materialised dotted path (e.g. `priorityOilRateStd.externalId`)
 *   produced by `transformDataset`'s nested-path support, or
 * - any other column that happens to hold a bare externalId string.
 *
 * Returns a deduped, insertion-ordered array. Rows with `null` / `undefined`
 * / unrecognised shapes at the named columns are skipped silently.
 *
 * @param rows - Dataset rows to scan.
 * @param columns - Column names to inspect on each row. When empty, the
 *   result is also empty — callers decide whether that's an error.
 */
export function extractExternalIdsFromRows(rows: readonly DatasetRow[], columns: readonly string[]): string[] {
  if (columns.length === 0) {
    return [];
  }
  const seen = new Set<string>();
  for (const row of rows) {
    for (const column of columns) {
      const value = row[column];
      if (value == null) {
        continue;
      }
      if (typeof value === 'string') {
        seen.add(value);
        continue;
      }
      if (isEnrichedTimeseriesRef(value)) {
        seen.add(value.externalId);
      }
    }
  }
  return [...seen];
}
